import React, { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { BaseStyleEnums, ReactTable } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, Row, SortingState } from '@tanstack/react-table';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { RankAccessor } from '../../enums';
import { StandingsSeason, TeamStandingsRecord } from '../../types';

import getStandingsColumns from './columns';
import { getStandingsRowClassName } from './utils';

import { StandingsTableContainer } from './styles';

type StandingsTableProps = {
    data: Array<TeamStandingsRecord>;
    displayedSeason: StandingsSeason;
    rankAccessor: RankAccessor;
    setSortingState: (state: SortingState) => void;
    sortingState: SortingState;
};

const StandingsTable: FC<StandingsTableProps> = ({
    data,
    displayedSeason,
    rankAccessor,
    setSortingState,
    sortingState,
}) => {
    const intl = useIntl();
    const size = useSizeWithThrottle();
    const isSmallScreen: boolean = size.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const visibleColumns: Array<ColumnDef<TeamStandingsRecord>> = useMemo(
        () => getStandingsColumns(rankAccessor, displayedSeason, isSmallScreen, intl),
        [rankAccessor, displayedSeason, isSmallScreen, intl]
    );

    // we treat teams with no games played as effectively unranked in the standings. setting their rank to undefined makes it easier to exclude them from table sorting
    const normalizedStandings = data.map((item) => {
        if (item.gamesPlayed === 0) {
            return {
                ...item,
                leagueSequence: undefined,
                divisionSequence: undefined,
                conferenceSequence: undefined,
                wildcardSequence: undefined,
            };
        }
        return item;
    });

    return (
        <StandingsTableContainer>
            <ReactTable
                columns={visibleColumns}
                data={normalizedStandings}
                sortingState={sortingState}
                getRowClassName={(row: Row<TeamStandingsRecord>) =>
                    getStandingsRowClassName(row.original, rankAccessor, sortingState)
                }
                onSortingChange={setSortingState}
            />
        </StandingsTableContainer>
    );
};

export default StandingsTable;
